import React, { useState, useEffect, useCallback } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import { getFirestore, collection, getDocs } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { FiShield, FiLogOut, FiAlertTriangle, FiRefreshCw } from 'react-icons/fi';
import app, { auth } from '../../config/firebase';
import '../AdminBusinesses.css';
import './AdminConsole.css';

const TABS = [
  { to: '/admin', label: 'Overview', end: true },
  { to: '/admin/businesses', label: 'Businesses' },
  { to: '/admin/users', label: 'Users' },
  { to: '/admin/categories', label: 'Categories' },
  { to: '/admin/payments', label: 'Payments' },
];

// Loads the console's data once and shares it with every tab via the outlet context.
export default function AdminLayout() {
  const navigate = useNavigate();
  const [loggingOut, setLoggingOut] = useState(false);
  const [logoutError, setLogoutError] = useState('');

  const [businesses, setBusinesses] = useState([]);
  const [users, setUsers] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [consoleError, setConsoleError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    setConsoleError('');
    const db = getFirestore(app);
    const getConsoleData = httpsCallable(getFunctions(app), 'getAdminConsoleData');

    const [businessResult, consoleResult] = await Promise.allSettled([
      getDocs(collection(db, 'businesses')),
      getConsoleData(),
    ]);

    if (businessResult.status === 'fulfilled') {
      setBusinesses(businessResult.value.docs.map(d => ({ id: d.id, ...d.data() })));
    } else {
      console.error('Error fetching businesses:', businessResult.reason);
      setError(`Couldn't load businesses: ${businessResult.reason.code || businessResult.reason.message}`);
    }

    if (consoleResult.status === 'fulfilled') {
      setUsers(consoleResult.value.data.users || []);
      setPayments(consoleResult.value.data.payments || []);
    } else {
      console.error('Error fetching admin console data:', consoleResult.reason);
      setConsoleError(`Couldn't load users and payments: ${consoleResult.reason.code || consoleResult.reason.message}`);
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleLogout = async () => {
    if (loggingOut) return;
    setLoggingOut(true);
    setLogoutError('');
    try {
      await signOut(auth);
      navigate('/', { replace: true });
    } catch (err) {
      console.error('Logout error:', err);
      setLogoutError('Failed to log out. Please try again.');
      setLoggingOut(false);
    }
  };

  const usersById = Object.fromEntries(users.map(u => [u.id, u]));
  const businessesById = Object.fromEntries(businesses.map(b => [b.id, b]));

  return (
    <div className="admin-page">
      <div className="admin-container">
        <div className="admin-page-header">
          <h1><FiShield /> Admin Console</h1>
          <div className="admin-header-actions">
            <button type="button" className="admin-logout-button" onClick={load} disabled={loading}>
              <FiRefreshCw aria-hidden="true" />
              <span>{loading ? 'Loading…' : 'Refresh'}</span>
            </button>
            <button type="button" className="admin-logout-button" onClick={handleLogout} disabled={loggingOut}>
              <FiLogOut aria-hidden="true" />
              <span>{loggingOut ? 'Logging out…' : 'Logout'}</span>
            </button>
          </div>
        </div>

        <nav className="admin-tabs" aria-label="Admin sections">
          {TABS.map(tab => (
            <NavLink
              key={tab.to}
              to={tab.to}
              end={tab.end}
              className={({ isActive }) => `filter-btn ${isActive ? 'active' : ''}`}
            >
              {tab.label}
            </NavLink>
          ))}
        </nav>

        {logoutError && <div className="form-error" role="alert"><FiAlertTriangle /> {logoutError}</div>}
        {error && <div className="form-error"><FiAlertTriangle /> {error}</div>}
        {consoleError && <div className="form-error"><FiAlertTriangle /> {consoleError}</div>}

        {loading ? <div role="status">Loading...</div> : (
          <Outlet context={{
            businesses, setBusinesses, users, setUsers, payments, usersById, businessesById, setError,
          }} />
        )}
      </div>
    </div>
  );
}
